/**
 * Self-test suite for the attachment size/type policy + scan hook.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentPolicySelfTest.swift`.
 *
 * Coverage:
 *
 *   A. MIME → category map. The size-cap lookup goes through the category,
 *      so a regression here would mis-budget every subsequent decision.
 *      A1. image/jpeg maps to image
 *      A2. video/mp4 maps to video
 *      A3. audio/opus maps to voice (not audio)
 *      A4. audio/mp4 maps to audio
 *      A5. application/pdf maps to pdf
 *      A6. text/plain maps to generic
 *
 *   B. Size budget. One allow-case at the cap, one reject 1 byte over.
 *      B1. image at cap is allowed
 *      B2. image 1 byte over cap rejects size_exceeded
 *      B3. video at cap is allowed
 *      B4. voice 1 byte over cap rejects size_exceeded
 *      B5. pdf at cap is allowed
 *      B6. generic 1 byte over cap rejects size_exceeded
 *
 *   C. Magic-byte sniff. Spoof attempts must produce mime_spoof; legit
 *      headers must pass.
 *      C1. valid PNG header is allowed
 *      C2. PNG-mime with JPEG bytes rejects mime_spoof
 *      C3. valid JPEG header is allowed
 *      C4. JPEG-mime with PDF bytes rejects mime_spoof
 *      C5. valid PDF header is allowed
 *      C6. valid WEBP header is allowed
 *      C7. opus mime accepts arbitrary bytes (no magic enforcement)
 *
 *   D. Combined decisions. Documents the ordering: size check fires before
 *      magic check, so an over-size payload with bad magic produces a
 *      size_exceeded rejection (not mime_spoof).
 *      D1. valid size + valid magic → allow
 *      D2. valid size + bad magic → reject mime_spoof
 *      D3. over-size + bad magic → size_exceeded fires first
 *
 *   E. DecompressionBombScanner. Clean, ratio-outlier, undershoot.
 *      E1. 1:1 ratio is clean
 *      E2. 50x claim is still clean (below 100x threshold)
 *      E3. 101x claim → suspicious (decompression-ratio outlier)
 *      E4. actual 2.1x more than claimed → suspicious (size-claim-undershoot)
 *      E5. coordinator returns first non-clean verdict in scanner order
 *
 *   F. Scanner composition. AlwaysClean alone, bomb-after-clean, empty list.
 *      F1. AlwaysCleanScanner alone returns clean
 *      F2. AlwaysClean before bomb scanner still detects bomb
 *      F3. empty scanner list returns clean
 */

import { SelfTestRunner, buildSuiteResult, fail, pass } from "../selftest";
import type { TestCase } from "../selftest";
import {
  AttachmentSizeBudget,
  categoryForMimeType,
  decide,
  mimeTypeFromString,
} from "./AttachmentPolicy";
import type {
  AttachmentMimeType,
  AttachmentPolicyDecision,
} from "./AttachmentPolicy";
import {
  AlwaysCleanScanner,
  AttachmentScanCoordinator,
  DecompressionBombScanner,
  scanSuspicious,
  verdictEquals,
} from "./AttachmentScanHook";
import type { AttachmentScanVerdict, AttachmentScanner } from "./AttachmentScanHook";
import type { AttachmentCategory } from "./AttachmentPolicy";

// ---------------------------------------------------------------------------
// Fixture helpers
// ---------------------------------------------------------------------------

function pngHeader(): Uint8Array {
  return new Uint8Array([0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a, 0x00, 0x00, 0x00, 0x0d]);
}

function jpegHeader(): Uint8Array {
  return new Uint8Array([0xff, 0xd8, 0xff, 0xe0, 0x00, 0x10, 0x4a, 0x46]);
}

function pdfHeader(): Uint8Array {
  return new TextEncoder().encode("%PDF-1.7\n");
}

function webpHeader(): Uint8Array {
  // RIFF<4 size bytes>WEBP
  const d = new Uint8Array(12);
  const enc = new TextEncoder();
  d.set(enc.encode("RIFF"), 0);
  d.set([0x00, 0x00, 0x00, 0x00], 4);
  d.set(enc.encode("WEBP"), 8);
  return d;
}

function ftypHeader(): Uint8Array {
  // <4 size bytes>ftyp<4 brand bytes>
  const d = new Uint8Array(12);
  d.set([0x00, 0x00, 0x00, 0x20], 0);
  d.set(new TextEncoder().encode("ftyp"), 4);
  d.set(new TextEncoder().encode("mp42"), 8);
  return d;
}

function isReject(d: AttachmentPolicyDecision, code: string): boolean {
  return d.kind === "reject" && d.rejection.code === code;
}

function isSuspicious(v: AttachmentScanVerdict | undefined, needleInReason: string): boolean {
  return v?.kind === "suspicious" && v.reason.includes(needleInReason);
}

/** Hard-coded verdict scanner for coordinator ordering tests. */
class FixedScanner implements AttachmentScanner {
  constructor(private readonly verdict: AttachmentScanVerdict) {}

  async scan(
    _category: AttachmentCategory,
    _plaintextBytes: Uint8Array,
    _claimedDeclaredSize: number,
  ): Promise<AttachmentScanVerdict> {
    return this.verdict;
  }
}

// ---------------------------------------------------------------------------
// A. MIME → category
// ---------------------------------------------------------------------------

function testCategoryImageJpeg(): TestCase {
  const got = categoryForMimeType("image/jpeg");
  const ok = got === "image";
  return ok ? pass("A1: image/jpeg maps to image") : fail("A1: image/jpeg maps to image", `got ${got}`);
}

function testCategoryVideoMp4(): TestCase {
  const got = categoryForMimeType("video/mp4");
  const ok = got === "video";
  return ok ? pass("A2: video/mp4 maps to video") : fail("A2: video/mp4 maps to video", `got ${got}`);
}

function testCategoryAudioOpusIsVoice(): TestCase {
  const got = categoryForMimeType("audio/opus");
  const ok = got === "voice";
  return ok
    ? pass("A3: audio/opus maps to voice (not audio)")
    : fail("A3: audio/opus maps to voice (not audio)", `got ${got}`);
}

function testCategoryAudioMp4(): TestCase {
  const got = categoryForMimeType("audio/mp4");
  const ok = got === "audio";
  return ok ? pass("A4: audio/mp4 maps to audio") : fail("A4: audio/mp4 maps to audio", `got ${got}`);
}

function testCategoryPdf(): TestCase {
  const got = categoryForMimeType("application/pdf");
  const ok = got === "pdf";
  return ok ? pass("A5: application/pdf maps to pdf") : fail("A5: application/pdf maps to pdf", `got ${got}`);
}

function testCategoryTextPlainIsGeneric(): TestCase {
  const got = categoryForMimeType("text/plain");
  const ok = got === "generic";
  return ok
    ? pass("A6: text/plain maps to generic")
    : fail("A6: text/plain maps to generic", `got ${got}`);
}

// ---------------------------------------------------------------------------
// B. Size budget
// ---------------------------------------------------------------------------

function testSizeImageAtCap(): TestCase {
  const cap = AttachmentSizeBudget.imageMax;
  const d = decide("image/png", cap, pngHeader());
  const ok = d.kind === "allow";
  return ok ? pass("B1: image at cap is allowed") : fail("B1: image at cap is allowed", `got ${d.kind}`);
}

function testSizeImageOverCap(): TestCase {
  const cap = AttachmentSizeBudget.imageMax;
  const d = decide("image/png", cap + 1, pngHeader());
  const ok = isReject(d, "size_exceeded");
  return ok
    ? pass("B2: image 1 byte over cap rejects size_exceeded")
    : fail("B2: image 1 byte over cap rejects size_exceeded", `got ${JSON.stringify(d)}`);
}

function testSizeVideoAtCap(): TestCase {
  const cap = AttachmentSizeBudget.videoMax;
  const d = decide("video/mp4", cap, ftypHeader());
  const ok = d.kind === "allow";
  return ok ? pass("B3: video at cap is allowed") : fail("B3: video at cap is allowed", `got ${d.kind}`);
}

function testSizeVoiceOverCap(): TestCase {
  const cap = AttachmentSizeBudget.voiceMax;
  const d = decide("audio/opus", cap + 1, new Uint8Array([0x00]));
  const ok = isReject(d, "size_exceeded");
  return ok
    ? pass("B4: voice 1 byte over cap rejects size_exceeded")
    : fail("B4: voice 1 byte over cap rejects size_exceeded", `got ${JSON.stringify(d)}`);
}

function testSizePdfAtCap(): TestCase {
  const cap = AttachmentSizeBudget.pdfMax;
  const d = decide("application/pdf", cap, pdfHeader());
  const ok = d.kind === "allow";
  return ok ? pass("B5: pdf at cap is allowed") : fail("B5: pdf at cap is allowed", `got ${d.kind}`);
}

function testSizeGenericOverCap(): TestCase {
  const cap = AttachmentSizeBudget.genericMax;
  const d = decide("text/plain", cap + 1, new TextEncoder().encode("hello world"));
  const ok = isReject(d, "size_exceeded");
  return ok
    ? pass("B6: generic 1 byte over cap rejects size_exceeded")
    : fail("B6: generic 1 byte over cap rejects size_exceeded", `got ${JSON.stringify(d)}`);
}

// ---------------------------------------------------------------------------
// C. Magic-byte sniff
// ---------------------------------------------------------------------------

function testMagicPngValid(): TestCase {
  const d = decide("image/png", 128, pngHeader());
  const ok = d.kind === "allow";
  return ok ? pass("C1: valid PNG header is allowed") : fail("C1: valid PNG header is allowed", `got ${d.kind}`);
}

function testMagicPngWithJpegBytesRejected(): TestCase {
  const d = decide("image/png", 128, jpegHeader());
  const ok = isReject(d, "mime_spoof");
  return ok
    ? pass("C2: PNG-mime with JPEG bytes rejects mime_spoof")
    : fail("C2: PNG-mime with JPEG bytes rejects mime_spoof", `got ${JSON.stringify(d)}`);
}

function testMagicJpegValid(): TestCase {
  const d = decide("image/jpeg", 128, jpegHeader());
  const ok = d.kind === "allow";
  return ok ? pass("C3: valid JPEG header is allowed") : fail("C3: valid JPEG header is allowed", `got ${d.kind}`);
}

function testMagicJpegWithPdfBytesRejected(): TestCase {
  const d = decide("image/jpeg", 128, pdfHeader());
  const ok = isReject(d, "mime_spoof");
  return ok
    ? pass("C4: JPEG-mime with PDF bytes rejects mime_spoof")
    : fail("C4: JPEG-mime with PDF bytes rejects mime_spoof", `got ${JSON.stringify(d)}`);
}

function testMagicPdfValid(): TestCase {
  const d = decide("application/pdf", 128, pdfHeader());
  const ok = d.kind === "allow";
  return ok ? pass("C5: valid PDF header is allowed") : fail("C5: valid PDF header is allowed", `got ${d.kind}`);
}

function testMagicWebpValid(): TestCase {
  const d = decide("image/webp", 128, webpHeader());
  const ok = d.kind === "allow";
  return ok ? pass("C6: valid WEBP header is allowed") : fail("C6: valid WEBP header is allowed", `got ${d.kind}`);
}

function testMagicGarbageBytesUnrecognised(): TestCase {
  // Garbage bytes presented as image/png — must be rejected as mime_spoof.
  const garbage = new Uint8Array([0xde, 0xad, 0xbe, 0xef, 0x00, 0x00, 0x00, 0x00]);
  const d = decide("image/png", 128, garbage);
  const ok = isReject(d, "mime_spoof");
  return ok
    ? pass("C7: garbage bytes claimed as image/png → mime_spoof (unrecognised magic)")
    : fail("C7: garbage bytes claimed as image/png → mime_spoof (unrecognised magic)", `got ${JSON.stringify(d)}`);
}

function testMagicOpusUnenforced(): TestCase {
  // audio/opus has no magic-byte enforcement (legitimate containers vary),
  // so arbitrary bytes must still pass.
  const d = decide("audio/opus", 128, new Uint8Array([0xde, 0xad, 0xbe, 0xef]));
  const ok = d.kind === "allow";
  return ok
    ? pass("C8: opus mime accepts arbitrary bytes (no magic enforcement)")
    : fail("C8: opus mime accepts arbitrary bytes (no magic enforcement)", `got ${d.kind}`);
}

// ---------------------------------------------------------------------------
// D. Combined decisions
// ---------------------------------------------------------------------------

function testCombinedValidSizeValidMagic(): TestCase {
  const d = decide("image/png", 1024, pngHeader());
  const ok = d.kind === "allow";
  return ok
    ? pass("D1: valid size + valid magic → allow")
    : fail("D1: valid size + valid magic → allow", `got ${d.kind}`);
}

function testCombinedValidSizeBadMagic(): TestCase {
  const d = decide("image/png", 1024, pdfHeader());
  const ok = isReject(d, "mime_spoof");
  return ok
    ? pass("D2: valid size + bad magic → reject mime_spoof")
    : fail("D2: valid size + bad magic → reject mime_spoof", `got ${JSON.stringify(d)}`);
}

function testCombinedOverSizeBadMagicSizeFiresFirst(): TestCase {
  // Documents the ordering: size check is intentionally first so a
  // 10GB blob is bounced before the magic sniff ever runs. Bad magic
  // is irrelevant once size has already failed.
  const d = decide("image/png", AttachmentSizeBudget.imageMax + 1, pdfHeader());
  const ok = isReject(d, "size_exceeded");
  return ok
    ? pass("D3: over-size + bad magic → size_exceeded fires first (not mime_spoof)")
    : fail("D3: over-size + bad magic → size_exceeded fires first (not mime_spoof)", `got ${JSON.stringify(d)}`);
}

// ---------------------------------------------------------------------------
// E. DecompressionBombScanner
// ---------------------------------------------------------------------------

async function testBombScannerOneToOne(): Promise<TestCase> {
  const bytes = new Uint8Array(1000).fill(0xab);
  const v = await new DecompressionBombScanner().scan("generic", bytes, 1000);
  const ok = v.kind === "clean";
  return ok
    ? pass("E1: decompression-bomb scanner: 1:1 ratio is clean")
    : fail("E1: decompression-bomb scanner: 1:1 ratio is clean", `got ${JSON.stringify(v)}`);
}

async function testBombScannerFiftyXClaim(): Promise<TestCase> {
  // 50x claim is below the 100x threshold — must still be clean.
  const bytes = new Uint8Array(1000).fill(0xab);
  const v = await new DecompressionBombScanner().scan("generic", bytes, 50_000);
  const ok = v.kind === "clean";
  return ok
    ? pass("E2: decompression-bomb scanner: 50x claim is still clean")
    : fail("E2: decompression-bomb scanner: 50x claim is still clean", `got ${JSON.stringify(v)}`);
}

async function testBombScannerRatioOutlier(): Promise<TestCase> {
  // 101x claim (1000 bytes actual, 101_000 claimed) → suspicious.
  const bytes = new Uint8Array(1000).fill(0xab);
  const v = await new DecompressionBombScanner().scan("generic", bytes, 101_000);
  const ok = isSuspicious(v, "decompression-ratio outlier");
  return ok
    ? pass("E3: decompression-bomb scanner: 101x claim → suspicious (decompression-ratio outlier)")
    : fail(
        "E3: decompression-bomb scanner: 101x claim → suspicious (decompression-ratio outlier)",
        `got ${JSON.stringify(v)}`,
      );
}

async function testBombScannerUndershoot(): Promise<TestCase> {
  // actual (2100) > claimed (1000) × 2 → size-claim-undershoot.
  const bytes = new Uint8Array(2100).fill(0xab);
  const v = await new DecompressionBombScanner().scan("generic", bytes, 1000);
  const ok = isSuspicious(v, "size-claim-undershoot");
  return ok
    ? pass("E4: decompression-bomb scanner: actual 2.1x more than claimed → suspicious (size-claim-undershoot)")
    : fail(
        "E4: decompression-bomb scanner: actual 2.1x more than claimed → suspicious (size-claim-undershoot)",
        `got ${JSON.stringify(v)}`,
      );
}

async function testCoordinatorReturnsFirstNonClean(): Promise<TestCase> {
  // Two scanners; first one fires .suspicious, second would fire .blocked.
  // We want the *first* non-clean — proves ordering.
  const coord = new AttachmentScanCoordinator([
    new FixedScanner(scanSuspicious("first")),
    new FixedScanner({ kind: "blocked", reason: "second" }),
  ]);
  const v = await coord.run("generic", new Uint8Array(0), 0);
  const expected = scanSuspicious("first");
  const ok = verdictEquals(v, expected);
  return ok
    ? pass("E5: coordinator returns first non-clean verdict in scanner order")
    : fail("E5: coordinator returns first non-clean verdict in scanner order", `got ${JSON.stringify(v)}`);
}

// ---------------------------------------------------------------------------
// F. Scanner composition
// ---------------------------------------------------------------------------

async function testCompositionAlwaysCleanAlone(): Promise<TestCase> {
  const coord = new AttachmentScanCoordinator([new AlwaysCleanScanner()]);
  const v = await coord.run("generic", new Uint8Array([0x00]), 1);
  const ok = v.kind === "clean";
  return ok
    ? pass("F1: AlwaysCleanScanner alone returns clean")
    : fail("F1: AlwaysCleanScanner alone returns clean", `got ${JSON.stringify(v)}`);
}

async function testCompositionBombAfterClean(): Promise<TestCase> {
  // Order matters: a leading AlwaysClean must not mask a later detector.
  const coord = new AttachmentScanCoordinator([
    new AlwaysCleanScanner(),
    new DecompressionBombScanner(),
  ]);
  const bytes = new Uint8Array(100).fill(0xab);
  const v = await coord.run("generic", bytes, 100_000);
  const ok = isSuspicious(v, "decompression-ratio outlier");
  return ok
    ? pass("F2: AlwaysClean before bomb scanner still detects bomb")
    : fail("F2: AlwaysClean before bomb scanner still detects bomb", `got ${JSON.stringify(v)}`);
}

async function testCompositionEmptyList(): Promise<TestCase> {
  const coord = new AttachmentScanCoordinator([]);
  const v = await coord.run("generic", new Uint8Array(0), 0);
  const ok = v.kind === "clean";
  return ok
    ? pass("F3: empty scanner list returns clean")
    : fail("F3: empty scanner list returns clean", `got ${JSON.stringify(v)}`);
}

// ---------------------------------------------------------------------------
// Bonus: mimeTypeFromString allow-list guard
// ---------------------------------------------------------------------------

function testMimeTypeFromStringKnown(): TestCase {
  const got = mimeTypeFromString("image/png");
  const ok = got === "image/png";
  return ok
    ? pass("G1: mimeTypeFromString accepts known MIME")
    : fail("G1: mimeTypeFromString accepts known MIME", `got ${String(got)}`);
}

function testMimeTypeFromStringUnknown(): TestCase {
  const got = mimeTypeFromString("application/x-evil");
  const ok = got === null;
  return ok
    ? pass("G2: mimeTypeFromString returns null for unknown MIME")
    : fail("G2: mimeTypeFromString returns null for unknown MIME", `got ${String(got)}`);
}

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

SelfTestRunner.register({
  name: "Attachment policy (size + type + scan)",
  isVerified: true,
  run: async () => {
    const syncCases: TestCase[] = [
      // A. MIME → category
      testCategoryImageJpeg(),
      testCategoryVideoMp4(),
      testCategoryAudioOpusIsVoice(),
      testCategoryAudioMp4(),
      testCategoryPdf(),
      testCategoryTextPlainIsGeneric(),
      // B. Size budget
      testSizeImageAtCap(),
      testSizeImageOverCap(),
      testSizeVideoAtCap(),
      testSizeVoiceOverCap(),
      testSizePdfAtCap(),
      testSizeGenericOverCap(),
      // C. Magic-byte sniff
      testMagicPngValid(),
      testMagicPngWithJpegBytesRejected(),
      testMagicJpegValid(),
      testMagicJpegWithPdfBytesRejected(),
      testMagicPdfValid(),
      testMagicWebpValid(),
      testMagicGarbageBytesUnrecognised(),
      testMagicOpusUnenforced(),
      // D. Combined decisions
      testCombinedValidSizeValidMagic(),
      testCombinedValidSizeBadMagic(),
      testCombinedOverSizeBadMagicSizeFiresFirst(),
      // G. Allow-list guard
      testMimeTypeFromStringKnown(),
      testMimeTypeFromStringUnknown(),
    ];

    const asyncCases: TestCase[] = await Promise.all([
      // E. DecompressionBombScanner
      testBombScannerOneToOne(),
      testBombScannerFiftyXClaim(),
      testBombScannerRatioOutlier(),
      testBombScannerUndershoot(),
      testCoordinatorReturnsFirstNonClean(),
      // F. Scanner composition
      testCompositionAlwaysCleanAlone(),
      testCompositionBombAfterClean(),
      testCompositionEmptyList(),
    ]);

    return buildSuiteResult([...syncCases, ...asyncCases]);
  },
});
